// Desktop frame: title bar, menu bar, toolbar, document tabs, settings banner and status bar.
import { useEffect, useState, type ReactNode } from 'react';
import type { Mode, SsMethod, Tactic, TaxYear } from '@divorceos/schema';
import { act } from './actions';
import { caseChecksum } from './checksum';
import {
  APP, APP_VERSION, ENGINE_VERSION, FC4055_OPTIONS, SS_OPTIONS, TACTICS, TAX_YEAR_OPTIONS, fcWord, partyLabels, periodWord, resolveFc4055, tacticShort, type Fc4055Setting,
} from './model';
import { useCaseView, type CaseView } from './caseView';
import { useActiveDoc, useDis, type Doc } from './store';
import { useAuth } from '../auth/authStore';

const IC: Record<string, ReactNode> = {
  new: <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4M12 11v6M9 14h6" /></>,
  open: <><path d="M3 7h6l2 2h10v10H3z" /><path d="M3 11h18" /></>,
  save: <><path d="M5 4h11l3 3v13H5z" /><path d="M8 4v5h7V4M8 20v-6h8v6" /></>,
  print: <><path d="M7 9V4h10v5" /><rect x="4" y="9" width="16" height="7" rx="1.5" /><path d="M7 14h10v6H7z" /></>,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" /></>,
  cols: <><rect x="3" y="5" width="18" height="14" rx="1.5" /><path d="M9 5v14M15 5v14" /></>,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .8-1 1.5v.7M12 17h.01" /></>,
  doc: <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4M9 12h6M9 16h6" /></>,
  imp: <><path d="M12 3v12M7 10l5 5 5-5M4 19h16" /></>,
  cmp: <><path d="M4 6h7v12H4zM13 6h7v12h-7z" /><path d="M7.5 10v4M16.5 10v4" /></>,
  home: <><path d="M3 11.5 12 4l9 7.5" /><path d="M5.5 10v10h13V10" /><path d="M10 20v-5h4v5" /></>,
  eye: <><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" /><circle cx="12" cy="12" r="3" /></>,
};
export const Icon = ({ name }: { name: string }) => <svg viewBox="0 0 24 24" aria-hidden="true">{IC[name]}</svg>;

/** Display name for a case tab/title: the client (folder) name when known, else the case name. */
export function useClientName(clientId: string | null | undefined): string | undefined {
  return useDis((s) => (clientId ? s.clientCache[clientId]?.name ?? s.clientsList.find((c) => c.id === clientId)?.name : undefined));
}

export function TitleBar() {
  const doc = useActiveDoc();
  const clientName = useClientName(doc?.clientId);
  const email = useAuth((s) => s.user?.email ?? '');
  const initial = (email || '?').charAt(0).toUpperCase();
  const title = doc ? `${clientName || doc.name || 'Untitled case'}${doc.caseId ? '' : ' (not saved yet)'}` : 'Start';
  return (
    <div className="titlebar">
      <a className="home-link" href="#/app" title="Go to the Start page" onClick={(e) => { e.preventDefault(); act('goStart'); }}>
        <span className="glyph">dis</span>
        <span className="ttl-app">{APP}</span>
      </a>
      <span className="ttl"><span>— {title}</span></span>
      <span className="sp" />
      <a className="back" href="#" title="Replay the guided tour" onClick={(e) => { e.preventDefault(); act('tour'); }}>Guide</a>
      <a className="back" href="../docs/">DivorceOS docs</a>
      <a className="account" href="#/app/profile" title={email ? `${email} — account & sign out` : 'Your account'}>
        <span className="pavatar">{initial}</span>
      </a>
    </div>
  );
}

type MenuItem = '-' | { label: string; cmd: string; key?: string; on?: boolean; group?: false } | { group: true; label: string };

export function MenuBar() {
  const menu = useDis((s) => s.ui.menu);
  const ui = useDis((s) => s.ui);
  const recent = useDis((s) => s.recent);
  const clients = useDis((s) => s.clientsList);
  const setUi = useDis((s) => s.setUi);
  const doc = useActiveDoc();
  const c = doc?.data;
  // Recent cases by CLIENT (folder): one entry per client, its most recent scenario, labelled by client name.
  const clientName = new Map(clients.map((cl) => [cl.id, cl.name]));
  const recentClients: typeof recent = [];
  const seenClients = new Set<string>();
  for (const r of recent) {
    const key = r.clientId ?? r.id;
    if (seenClients.has(key)) continue;
    seenClients.add(key);
    recentClients.push(r);
    if (recentClients.length >= 5) break;
  }
  const menus: [string, MenuItem[]][] = [
    ['File', [
      { label: 'New case', cmd: 'newCase' }, { label: 'Open…', cmd: 'openDlg' }, { label: 'Save', cmd: 'save', key: 'Ctrl+S' },
      { label: 'Save settings as my defaults', cmd: 'saveDefault' }, { label: 'Close case', cmd: 'closeCase' }, '-',
      { label: 'Client questionnaire…', cmd: 'questionnaire' }, { label: 'Import from document…', cmd: 'import' }, { label: 'Export to Excel…', cmd: 'exportXlsx' }, '-',
      { label: 'Print bench report', cmd: 'print:bench', key: 'Ctrl+P' }, '-', { group: true, label: 'Recent cases' },
      ...recentClients.map((r): MenuItem => ({ label: clientName.get(r.clientId ?? '') ?? r.name ?? 'Untitled case', cmd: `open:${r.id}` })),
    ]],
    ['Edit', [
      { label: 'Settings…', cmd: 'settings', key: 'S' }, { label: 'Children', cmd: 'tab:children', key: '2' }, '-',
      { label: 'Expand all rows', cmd: 'expandAll', key: 'X' }, { label: 'Collapse all rows', cmd: 'collapseAll' },
      { label: 'Restore automatic dependents', cmd: 'restoreAuto', key: 'Del' }, { label: 'Restore default settings', cmd: 'restoreDefaults' }, '-',
      { label: `${c?.settings.displayLowIncomeRange ? 'Hide' : 'Show'} low-income range`, cmd: 'toggleLia', key: 'L' },
    ]],
    ['View', [
      { label: 'Results', cmd: 'r:results', key: 'R' }, { label: 'Cash Flow', cmd: 'r:cash', key: 'F' }, { label: 'Notes', cmd: 'r:notes', key: 'N' }, { label: 'Help', cmd: 'r:help', key: 'H' }, '-',
      { label: 'Monthly', cmd: 'per:M', key: 'M', on: c?.mode === 'MONTHLY' }, { label: 'Yearly', cmd: 'per:Y', on: c?.mode === 'ANNUAL' },
      { label: 'Next tax year', cmd: 'nextYear', key: 'Y' }, { label: 'Switch FC 4055 version', cmd: 'toggle343', key: 'G' }, '-',
      { label: 'Hide zero rows', cmd: 'toggleZero', key: 'Z', on: ui.hideZeroGrid }, { label: 'Auto-recalculate', cmd: 'toggleAuto', on: ui.autoRecalc },
      { label: 'Field tooltips', cmd: 'toggleTips', on: ui.tooltips }, { label: 'Domestic partner labels', cmd: 'toggleRdp', on: !!c?.settings.domesticPartnership },
    ]],
    ['Reports', [
      { label: 'Preview all outputs', cmd: 'preview', key: 'O' }, '-', { label: 'Bench report', cmd: 'bench', key: 'P' }, { label: 'Snapshot comparison', cmd: 'rep:compare' }, { label: 'Add current figures as a snapshot', cmd: 'addScenario', key: 'C' },
      { label: 'Input variation', cmd: 'rep:variation', key: 'V' }, { label: 'Smith/Ostler bonus attachment', cmd: 'rep:smith', key: 'B' },
      { label: 'Exemption releases', cmd: 'rep:releases', key: 'E' }, { label: 'Tax form viewer…', cmd: 'forms', key: 'W' }, '-',
      { label: 'Compare with Xspouse…', cmd: 'compare' }, { label: 'All reports…', cmd: 'tab:reports', key: '9' },
    ]],
    ['Help', [
      { label: 'Help for selected field', cmd: 'r:help', key: 'H' }, { label: 'Keyboard shortcuts', cmd: 'keys' },
      { label: 'What’s new: FC 4055 from 1 Sep 2024', cmd: 'whatsnew' }, '-', { label: 'About disOS (case checksum)', cmd: 'about' },
    ]],
  ];
  return (
    <div className="menubar" role="menubar">
      {menus.map(([title, items], i) => (
        <div key={title} className={`m${menu === i ? ' open' : ''}`}>
          <button type="button" aria-haspopup="true" aria-expanded={menu === i} onClick={() => setUi({ menu: menu === i ? null : i })} onMouseEnter={() => { if (menu !== null && menu !== i) setUi({ menu: i }); }}>{title}</button>
          <div className="dropdown" role="menu">
            {items.map((it, j) => {
              if (it === '-') return <hr key={j} />;
              if (it.group) return <div key={j} className="lbl">{it.label}</div>;
              return (
                <button key={j} type="button" role="menuitem" className={it.on ? 'on' : ''} onClick={() => act(it.cmd)}>
                  {it.label}{it.key && <kbd>{it.key}</kbd>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Toolbar() {
  const doc = useActiveDoc();
  const update = useDis((s) => s.update);
  const c = doc?.data;
  const labels: [string, string] = doc ? partyLabels(doc.data, doc.prefs.labels) : ['Party A', 'Party B'];
  return (
    <div className="toolbar">
      <button className="tb-btn tb-home" type="button" title="Home: go to the Start page" onClick={() => act('goStart')} disabled={!doc}><Icon name="home" /><span className="t">Home</span></button>
      <span className="tb-sep" aria-hidden="true" />
      <button className="tb-btn" type="button" title="New case" onClick={() => act('newCase')}><Icon name="new" /></button>
      <button className="tb-btn" type="button" title="Open a case" onClick={() => act('openDlg')}><Icon name="open" /></button>
      <button className="tb-btn" type="button" title={doc ? `Save (${doc.saveStatus})` : 'Save'} disabled={!doc} onClick={() => act('save')}><Icon name="save" /><span className="t opt">{doc ? saveWord(doc.saveStatus) : 'Save'}</span></button>
      <button className="tb-btn" type="button" title="Print bench report" disabled={!doc} onClick={() => act('print:bench')}><Icon name="print" /></button>
      <button className="tb-btn tb-pri" type="button" title={doc ? 'Preview every report and tax form of this case (O)' : 'Open a case to preview its reports'} disabled={!doc} onClick={() => act('preview')}><Icon name="eye" /><span className="t">Preview</span></button>
      <span className="tb-sep" />
      {c && (
        <>
          <span className="tb-field"><label htmlFor="tbSS">SS</label>
            <select id="tbSS" title="County temporary spousal-support formula" value={c.settings.ssMethod} onChange={(e) => update((x) => { x.settings.ssMethod = e.target.value as SsMethod; })}>
              {SS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></span>
          <Fc4055Picker />
          <span className="tb-field"><label htmlFor="tbYear" className="opt">Year</label>
            <select id="tbYear" title="Tax year (Y)" value={c.taxYear} onChange={(e) => update((x) => { x.taxYear = Number(e.target.value) as TaxYear; })}>
              {TAX_YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select></span>
          <span className="tb-field">
            <select id="tbPer" aria-label="Period" title="Monthly or Yearly (M)" value={c.mode} onChange={(e) => update((x) => { x.mode = e.target.value as Mode; })}>
              <option value="MONTHLY">Monthly</option><option value="ANNUAL">Yearly</option>
            </select></span>
          <span className="tb-field"><label htmlFor="tbTac" className="opt">Tactic</label>
            <select id="tbTac" title="Tactic for the Proposed panel (T)" className={c.proposed.tactic !== '9' ? 'chg' : ''} value={c.proposed.tactic} onChange={(e) => update((x) => { x.proposed.tactic = e.target.value as Tactic; })}>
              {TACTICS.map((t) => <option key={t.id} value={t.id}>{t.id} · {tacticShort(t.id, labels)}</option>)}
            </select></span>
          <span className="tb-sep" />
          <button className="tb-btn" type="button" title="Settings (S)" onClick={() => act('settings')}><Icon name="gear" /><span className="t opt">Settings</span></button>
          <button className="tb-btn" type="button" title="Add current figures as a snapshot (C)" onClick={() => act('addScenario')}><Icon name="cols" /><span className="t opt">Add snapshot</span></button>
          <button className="tb-btn" type="button" title="Tax form viewer (W)" onClick={() => act('forms')}><Icon name="doc" /><span className="t opt opt2">Tax forms</span></button>
          <button className="tb-btn" type="button" title="Compare with Xspouse (reference engine)" onClick={() => act('compare')}><Icon name="cmp" /><span className="t opt opt2">Compare</span></button>
        </>
      )}
      <span className="grow" />
      <button className="tb-btn" type="button" title="Help (H)" onClick={() => act(doc ? 'r:help' : 'about')}><Icon name="help" /></button>
    </div>
  );
}

const saveWord = (s: string): string => ({ new: 'Save', dirty: 'Save', saving: 'Saving…', saved: 'Saved', error: 'Retry save' }[s] ?? 'Save');

function Fc4055Picker() {
  const doc = useActiveDoc();
  const update = useDis((s) => s.update);
  if (!doc) return null;
  const v = (doc.data.settings.fc4055Version ?? 'PRE_SB343') as Fc4055Setting;
  const auto = FC4055_OPTIONS.map(([k, l]) => [k, k === 'AUTO' ? `Auto (${fcWord(autoOf(doc.data))} 9/1/2024)` : l] as const);
  return (
    <span className="tb-field"><label htmlFor="tbCS">FC 4055</label>
      <select id="tbCS" title="FC 4055 version (G)" className={v !== 'AUTO' ? 'chg' : ''} value={v} onChange={(e) => update((x) => { x.settings.fc4055Version = e.target.value as Fc4055Setting; })}>
        {auto.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select></span>
  );
}
const autoOf = (c: Parameters<typeof resolveFc4055>[0]) => resolveFc4055({ taxYear: c.taxYear, settings: { ...c.settings, fc4055Version: 'AUTO' } });

export function DocTabs() {
  const docs = useDis((s) => s.docs);
  const active = useDis((s) => s.active);
  const activate = useDis((s) => s.activate);
  const closeDoc = useDis((s) => s.closeDoc);
  return (
    <div className="doctabs" role="tablist" aria-label="Open cases">
      <button type="button" role="tab" className={`dt start${active === 'start' ? ' on' : ''}`} aria-selected={active === 'start'} onClick={() => act('goStart')}><span className="nm">Start</span></button>
      {docs.map((d) => (
        <DocTab key={d.key} d={d} active={d.key === active} onActivate={() => activate(d.key)} onClose={() => closeDoc(d.key)} />
      ))}
    </div>
  );
}

function DocTab({ d, active, onActivate, onClose }: { d: Doc; active: boolean; onActivate: () => void; onClose: () => void }) {
  const clientName = useClientName(d.clientId);
  const name = clientName || d.name || 'Untitled case';
  return (
    <div role="tab" tabIndex={0} className={`dt${active ? ' on' : ''}`} aria-selected={active}
      onClick={onActivate} onKeyDown={(e) => { if (e.key === 'Enter') onActivate(); }}>
      <span className="dot" title={d.saveStatus} style={d.saveStatus === 'error' ? { background: 'var(--red)' } : d.saveStatus === 'dirty' || d.saveStatus === 'saving' ? { background: 'var(--amber)' } : undefined} />
      <span className="nm">{name} <span className="muted">({d.data.taxYear})</span></span>
      <button type="button" className="x" title="Close case (it stays saved)" aria-label={`Close ${name}`} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>
    </div>
  );
}

export function Banner() {
  const v = useCaseView();
  // CRC 5.275(c): the screen must prominently indicate whether the calculation uses default settings.
  if (!v.changed.length) {
    return (
      <div className="banner default" role="status">
        <b>Default settings</b>
        <span className="lst">This calculation uses the program default settings.</span>
      </div>
    );
  }
  return (
    <div className="banner changed" role="status">
      <b>{v.changed.length} {v.changed.length === 1 ? 'setting differs' : 'settings differ'} from your defaults</b>
      <span className="lst">{v.changed.map((x) => `${x.label}: ${x.value}`).join(' · ')}</span>
      <button type="button" onClick={() => act('settings')}>Review</button>
      <button type="button" onClick={() => act('restoreDefaults')}>Restore defaults</button>
    </div>
  );
}

/** Case checksum (async SHA-256), recomputed when the inputs change. */
export function useChecksum(v: CaseView | null): string {
  const [sum, setSum] = useState('…');
  const data = v?.calc;
  useEffect(() => {
    if (!data) return;
    let live = true;
    caseChecksum(data, ENGINE_VERSION).then((r) => { if (live) setSum(r.short); }).catch(() => { if (live) setSum('unavailable'); });
    return () => { live = false; };
  }, [data]);
  return sum;
}

export function StatusBar({ v }: { v: CaseView | null }) {
  const autoRecalc = useDis((s) => s.ui.autoRecalc);
  const sum = useChecksum(v);
  if (!v) return <div className="statusbar"><span>Ready</span><span className="sp" /><span>Published IRS and FTB tables, {TAX_YEAR_OPTIONS[0]} – {TAX_YEAR_OPTIONS[TAX_YEAR_OPTIONS.length - 2]} (2027 provisional)</span><span>{APP_VERSION}</span></div>;
  const t = new Date(v.doc.calcAt);
  const tm = t.toLocaleTimeString('en-GB');
  const save = v.doc.saveStatus === 'error' ? <span className="bad">Save failed: {v.doc.saveError}</span> : <span>{v.doc.caseId ? saveWord(v.doc.saveStatus) === 'Saved' ? 'Saved' : v.doc.saveStatus === 'saving' ? 'Saving…' : 'Unsaved changes' : 'Not saved yet'}</span>;
  return (
    <div className="statusbar">
      {v.stale
        ? <><span className="bad">Inputs changed, not yet recalculated</span><button type="button" onClick={() => act('recalc')}>Recalculate (F9)</button></>
        : v.engineError ? <span className="bad">Engine error: {v.engineError}</span> : <span className="ok">● Calculated {tm}{autoRecalc ? ' · auto' : ''}</span>}
      {save}
      <span>Checksum <code>{sum}</code></span>
      <span className="sp" />
      <span>Tax tables {v.c.taxYear} (published)</span>
      <span>FC 4055 {fcWord(v.fcApplied)} 1 Sep 2024</span>
      <span>{periodWord(v.c)}</span>
      <span>{APP_VERSION}</span>
    </div>
  );
}
